import { useMutation, useQueryClient } from '@tanstack/react-query';

import { metasApi, metasKeys } from '../metas.api';
import type { EvaluationPayload, GoalPayload, GoalSettings, GoalStatus } from '../metas.types';

/**
 * Mutaciones del módulo de metas.
 *
 * Cualquier cambio (crear, editar, evaluar, recalcular) afecta al listado, al
 * panel y a los reportes, así que se invalida la raíz `metas` completa.
 */
function useInvalidarMetas() {
  const queryClient = useQueryClient();

  return (id?: number) => {
    void queryClient.invalidateQueries({ queryKey: metasKeys.all });

    if (id) {
      void queryClient.invalidateQueries({ queryKey: metasKeys.detalle(id) });
    }
  };
}

export function useCrearMeta() {
  const invalidar = useInvalidarMetas();

  return useMutation({
    mutationFn: (payload: GoalPayload) => metasApi.crear(payload),
    onSuccess: () => invalidar(),
  });
}

export function useActualizarMeta() {
  const invalidar = useInvalidarMetas();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: GoalPayload }) =>
      metasApi.actualizar(id, payload),
    onSuccess: (_data, variables) => invalidar(variables.id),
  });
}

export function useEliminarMeta() {
  const invalidar = useInvalidarMetas();

  return useMutation({
    mutationFn: (id: number) => metasApi.eliminar(id),
    onSuccess: () => invalidar(),
  });
}

/** Recalcula el avance con la operación real y guarda el resultado. */
export function useRecalcularMeta() {
  const invalidar = useInvalidarMetas();

  return useMutation({
    mutationFn: (id: number) => metasApi.recalcular(id),
    onSuccess: (_data, id) => invalidar(id),
  });
}

export function useEvaluarMeta() {
  const invalidar = useInvalidarMetas();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: EvaluationPayload }) =>
      metasApi.evaluar(id, payload),
    onSuccess: (_data, variables) => invalidar(variables.id),
  });
}

export function useCambiarEstadoMeta() {
  const invalidar = useInvalidarMetas();

  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: GoalStatus }) =>
      metasApi.cambiarEstado(id, status),
    onSuccess: (_data, variables) => invalidar(variables.id),
  });
}

export function useMarcarCumplida() {
  const invalidar = useInvalidarMetas();

  return useMutation({
    mutationFn: ({ id, userId }: { id: number; userId: number | null }) =>
      metasApi.marcarCumplida(id, userId),
    onSuccess: (_data, variables) => invalidar(variables.id),
  });
}

export function useGuardarConfiguracionMetas() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (settings: Partial<GoalSettings>) => metasApi.guardarConfiguracion(settings),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: metasKeys.configuracion });
    },
  });
}
